import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Lock, AlertTriangle, Landmark, Share2, FileText, Download, Sparkles, Copy as CopyIcon, RotateCw, Users, Send, Trash2, Check, Activity as ActivityIcon } from "lucide-react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ShareDialog } from "@/components/shared/ShareDialog";
import { api, API, getErrMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deal, setDeal] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [open, setOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [ticket, setTicket] = useState("");
  const [notes, setNotes] = useState("");
  const [tsOpen, setTsOpen] = useState(false);
  const [tsLoading, setTsLoading] = useState(false);
  const [tsDraft, setTsDraft] = useState("");
  const [tsGeneratedAt, setTsGeneratedAt] = useState(null);
  const [tsCached, setTsCached] = useState(false);
  const [tsActiveId, setTsActiveId] = useState(null);
  const [tsVersions, setTsVersions] = useState([]);
  const [tsScope, setTsScope] = useState("personal"); // 'personal' | 'shared'
  const [tsShared, setTsShared] = useState(null); // {version_number, generated_by, active_version_id}
  const [tsSending, setTsSending] = useState(false);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    api.get(`/deals/${id}`).then((r) => setDeal(r.data));
    api.get(`/deals/${id}/attachments`).then((r) => setAttachments(r.data)).catch(() => setAttachments([]));
    api.get(`/deals/${id}/activity`).then((r) => setActivity(r.data.events || [])).catch(() => setActivity([]));
  }, [id]);

  const refreshActivity = async () => {
    try {
      const r = await api.get(`/deals/${id}/activity`);
      setActivity(r.data.events || []);
    } catch {
      /* non-critical */
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const token = localStorage.getItem("frontera_token");
      const res = await fetch(`${API}/deals/${id}/attachments/${att.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Download failed", { description: err.message });
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return "";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const applyActive = (d, cached) => {
    setTsDraft(d.draft || "");
    setTsGeneratedAt(d.generated_at || null);
    setTsActiveId(d.active_version_id || null);
    setTsCached(!!cached);
  };

  const refreshVersions = async () => {
    try {
      const r = await api.get(`/deals/${id}/termsheet/versions`);
      setTsVersions(r.data.versions || []);
    } catch {
      setTsVersions([]);
    }
    try {
      const s = await api.get(`/deals/${id}/termsheet/shared`);
      setTsShared(s.data);
    } catch {
      setTsShared(null);
    }
  };

  const generateTermsheet = async (force = false) => {
    setTsOpen(true);
    setTsScope("personal");
    if (!force && !tsDraft) {
      try {
        const cached = await api.get(`/deals/${id}/termsheet/draft`);
        applyActive(cached.data, true);
        await refreshVersions();
        return;
      } catch {
        // 404 — fall through to generate
      }
    }
    setTsLoading(true);
    try {
      const r = await api.post(`/deals/${id}/termsheet/draft${force ? "?force=true" : ""}`);
      applyActive(r.data, !!r.data.cached);
      await refreshVersions();
      refreshActivity();
    } catch (err) {
      toast.error("Draft failed", { description: getErrMsg(err) });
      setTsOpen(false);
    } finally {
      setTsLoading(false);
    }
  };

  const regenerateTermsheet = () => generateTermsheet(true);

  const activateVersion = async (versionId) => {
    try {
      const r = await api.post(`/deals/${id}/termsheet/versions/${versionId}/activate`);
      applyActive(r.data, true);
      setTsScope("personal");
      await refreshVersions();
      refreshActivity();
    } catch (err) {
      toast.error("Switch failed", { description: getErrMsg(err) });
    }
  };

  const deleteVersion = async (versionId) => {
    if (tsVersions.length <= 1) {
      toast.error("Cannot delete the only version");
      return;
    }
    if (!window.confirm("Delete this version?")) return;
    try {
      await api.delete(`/deals/${id}/termsheet/versions/${versionId}`);
      await refreshVersions();
      // re-load active
      const cached = await api.get(`/deals/${id}/termsheet/draft`);
      applyActive(cached.data, true);
      refreshActivity();
      toast.success("Version removed");
    } catch (err) {
      toast.error("Delete failed", { description: getErrMsg(err) });
    }
  };

  const loadSharedDraft = async () => {
    try {
      const r = await api.get(`/deals/${id}/termsheet/shared`);
      setTsDraft(r.data.draft || "");
      setTsGeneratedAt(r.data.generated_at || null);
      setTsActiveId(r.data.active_version_id || null);
      setTsCached(true);
      setTsScope("shared");
    } catch (err) {
      toast.error("No shared draft", { description: getErrMsg(err) });
    }
  };

  const publishShared = async () => {
    if (!tsActiveId) return;
    if (user?.tier !== "institutional") {
      toast.error("Institutional tier required to publish");
      return;
    }
    if (!window.confirm("Publish this version as the deal's shared draft? All members with deal access will see it.")) return;
    try {
      const r = await api.post(`/deals/${id}/termsheet/publish`, { version_id: tsActiveId });
      setTsShared(r.data);
      refreshActivity();
      toast.success(`Published as shared draft v${r.data.version_number}`);
    } catch (err) {
      toast.error("Publish failed", { description: getErrMsg(err) });
    }
  };

  const sendToCounterparty = async () => {
    if (user?.tier !== "institutional") {
      toast.error("Institutional tier required");
      return;
    }
    const ok = window.confirm(
      `Render the ${tsScope} term-sheet draft as a PDF and attach it to the Deal Room (institutional tier)?`
    );
    if (!ok) return;
    setTsSending(true);
    try {
      const r = await api.post(`/deals/${id}/termsheet/send-to-counterparty`, { scope: tsScope });
      toast.success(`Attached: ${r.data.attachment.title}`);
      // refresh deal attachments
      const atts = await api.get(`/deals/${id}/attachments`);
      setAttachments(atts.data);
      refreshActivity();
    } catch (err) {
      toast.error("Send failed", { description: getErrMsg(err) });
    } finally {
      setTsSending(false);
    }
  };

  const copyTermsheet = async () => {
    try {
      await navigator.clipboard.writeText(tsDraft);
      toast.success("Term sheet copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadTermsheet = () => {
    const blob = new Blob([tsDraft], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const safe = (deal?.title || "termsheet").replace(/[^A-Za-z0-9]+/g, "-").slice(0, 60);
    a.download = `${safe}-termsheet-draft.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const formatRelative = (iso) => {
    if (!iso) return "";
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.round(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.round(hrs / 24);
    return `${days}d ago`;
  };

  const shareText = deal
    ? `${deal.title} — ${deal.country} · ${deal.size_usd} · ${deal.return_text}. Access frontier market deals before the market prices them.`
    : "";

  const submitAllocation = async () => {
    try {
      await api.post(`/deals/${id}/allocation`, { ticket_size: ticket, notes });
      toast.success("Allocation request submitted", { description: "Our team will reach out within 24–48h." });
      refreshActivity();
      setOpen(false);
      setTicket("");
      setNotes("");
    } catch (err) {
      toast.error("Could not submit", { description: getErrMsg(err) });
    }
  };

  if (!deal) {
    return <DashboardLayout><div className="p-10 text-muted-foreground">Loading…</div></DashboardLayout>;
  }

  if (deal.locked) {
    return (
      <DashboardLayout>
        <div className="px-6 md:px-10 py-10">
          <button onClick={() => navigate("/deals")} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1" data-testid="back-to-deals">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="mt-10 card-institutional p-10 text-center max-w-xl mx-auto">
            <Lock className="h-8 w-8 gold-text mx-auto mb-4" strokeWidth={1.5} />
            <h1 className="font-display font-medium text-2xl">{deal.title}</h1>
            <p className="text-sm text-muted-foreground mt-3">
              This deal requires <span className="gold-text capitalize">{deal.tier_required}</span> tier access.
              Your current tier is <span className="capitalize">{user?.tier || "standard"}</span>.
            </p>
            <button onClick={() => navigate("/pricing")} className="btn-primary mt-6" data-testid="upgrade-button">
              Upgrade Access <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="border-b border-border px-6 md:px-10 py-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button onClick={() => navigate("/deals")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-3" data-testid="back-to-deals">
            <ArrowLeft className="h-3 w-3" /> Deal Flow
          </button>
          <div className="flex items-center gap-3 mb-2">
            <Landmark className="h-5 w-5 gold-text" strokeWidth={1.5} />
            <span className="overline">{deal.country} · {deal.sector.replaceAll("_", " ")}</span>
          </div>
          <h1 className="font-display font-medium text-3xl md:text-4xl tracking-tight">{deal.title}</h1>
          <div className="flex items-center gap-3 mt-3">
            <StatusBadge status={deal.status} />
            <span className="text-xs text-muted-foreground">Updated {new Date(deal.updated_at).toLocaleDateString()}</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {(user?.tier === "professional" || user?.tier === "institutional") && (
            <button
              onClick={generateTermsheet}
              className="btn-ghost"
              data-testid="generate-termsheet-button"
              title="AI-drafted indicative term sheet"
            >
              <Sparkles className="h-4 w-4" /> Draft Term Sheet
            </button>
          )}
          <button
            onClick={() => setShareOpen(true)}
            className="btn-ghost"
            data-testid="share-deal-button"
          >
            <Share2 className="h-4 w-4" /> Share
          </button>
          <button onClick={() => setOpen(true)} className="btn-primary" data-testid="request-allocation-button">
            Request Allocation <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="px-6 md:px-10 py-8 grid lg:grid-cols-[1fr_320px] gap-8">
        <div className="space-y-10">
          {/* Metrics */}
          <div className="card-institutional p-6 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { k: "Size", v: deal.size_usd },
              { k: "Tenor", v: deal.tenor },
              { k: "Return", v: deal.return_text },
              { k: "Min Ticket", v: deal.min_ticket },
            ].map((m) => (
              <div key={m.k}>
                <div className="overline">{m.k}</div>
                <div className="font-mono text-xl md:text-2xl mt-2 gold-text">{m.v}</div>
              </div>
            ))}
          </div>

          <section>
            <h2 className="font-display font-medium text-xl mb-3">Overview</h2>
            <p className="text-muted-foreground leading-relaxed">{deal.overview}</p>
          </section>

          <section>
            <h2 className="font-display font-medium text-xl mb-4">Structure</h2>
            <p className="text-sm mb-5">{deal.structure}</p>
            <div className="card-institutional p-6 flex items-center gap-3 flex-wrap">
              {deal.structure_flow?.map((n, i) => (
                <React.Fragment key={n}>
                  <div className="px-4 py-2.5 border border-border rounded font-mono text-xs bg-[hsl(var(--surface-elevated))]">{n}</div>
                  {i < deal.structure_flow.length - 1 && <ArrowRight className="h-4 w-4 gold-text" />}
                </React.Fragment>
              ))}
            </div>
          </section>

          <section>
            <h2 className="font-display font-medium text-xl mb-4 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 gold-text" /> Risks
            </h2>
            <ul className="space-y-2">
              {deal.risks?.map((r) => (
                <li key={r} className="text-sm flex gap-3">
                  <span className="text-muted-foreground">·</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="font-display font-medium text-xl mb-4">Pricing Insight</h2>
            <div className="card-institutional p-6 bg-[hsl(var(--gold-glow))] border-[hsl(var(--gold))]">
              <p className="text-sm">{deal.pricing_insight}</p>
            </div>
          </section>

          <section>
            <h2 className="font-display font-medium text-xl mb-4">Execution Path</h2>
            <ol className="space-y-3">
              {deal.execution_path?.map((step, i) => (
                <li key={i} className="flex gap-4 text-sm">
                  <div className="h-6 w-6 shrink-0 border border-[hsl(var(--gold))] rounded-full flex items-center justify-center gold-text font-mono text-xs">{i + 1}</div>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </section>

          <section data-testid="deal-room-documents">
            <h2 className="font-display font-medium text-xl mb-4 flex items-center gap-2">
              <FileText className="h-4 w-4 gold-text" strokeWidth={1.5} /> Deal Room Documents
            </h2>
            {attachments.length === 0 ? (
              <div className="card-institutional p-6 text-sm text-muted-foreground">
                No documents attached yet. Term sheets and compliance memos appear here once released by the desk.
              </div>
            ) : (
              <div className="card-institutional divide-y divide-border">
                {attachments.map((att) => {
                  const gated = att.accessible === false;
                  return (
                    <div key={att.id} className="p-4 flex items-center justify-between gap-4" data-testid={`attachment-row-${att.id}`}>
                      <div className="flex items-start gap-3 min-w-0">
                        <FileText className="h-4 w-4 gold-text shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{att.title}</div>
                          <div className="text-xs text-muted-foreground mt-0.5 font-mono truncate">
                            {att.filename} · {formatSize(att.size)} ·
                            <span className="capitalize"> {att.tier_required} tier</span>
                          </div>
                        </div>
                      </div>
                      {gated ? (
                        <button
                          onClick={() => navigate("/pricing")}
                          className="btn-ghost shrink-0"
                          data-testid={`attachment-upgrade-${att.id}`}
                        >
                          <Lock className="h-3.5 w-3.5" /> Upgrade
                        </button>
                      ) : (
                        <button
                          onClick={() => downloadAttachment(att)}
                          className="btn-ghost shrink-0"
                          data-testid={`attachment-download-${att.id}`}
                        >
                          <Download className="h-3.5 w-3.5" /> Download
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section data-testid="deal-activity-section">
            <h2 className="font-display font-medium text-xl mb-4 flex items-center gap-2">
              <ActivityIcon className="h-4 w-4 gold-text" strokeWidth={1.5} /> Activity
              {activity.length > 0 && (
                <span className="font-mono text-[10px] text-muted-foreground ml-1">
                  · {activity.length}
                </span>
              )}
            </h2>
            {activity.length === 0 ? (
              <div className="card-institutional p-6 text-sm text-muted-foreground">
                No activity recorded yet. Term-sheet drafts, uploads, and allocation requests will appear here.
              </div>
            ) : (
              <div className="card-institutional divide-y divide-border max-h-[420px] overflow-y-auto">
                {activity.map((e) => (
                  <div key={e.id} className="p-4 flex items-start gap-3 text-sm" data-testid={`activity-row-${e.type}`}>
                    <div className="shrink-0 mt-1">
                      <span className={`block h-1.5 w-1.5 rounded-full ${
                        e.type === "sent_to_counterparty" || e.type === "shared_published"
                          ? "bg-[hsl(var(--gold))]"
                          : e.type === "deal_updated" || e.type === "attachment_deleted" || e.type === "version_deleted"
                          ? "bg-muted-foreground/60"
                          : "bg-[hsl(var(--status-live))]"
                      }`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="leading-snug">{e.message}</div>
                      <div className="mt-1 flex items-center gap-2 font-mono text-[10px] text-muted-foreground">
                        <span className="uppercase tracking-wider">{e.type.replaceAll("_", " ")}</span>
                        <span>·</span>
                        <span>{new Date(e.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Sticky side CTA */}
        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card-institutional p-6">
            <div className="overline gold-text">Deal Status</div>
            <div className="mt-2"><StatusBadge status={deal.status} /></div>
            <div className="divider my-5" />
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Min Ticket</span><span className="font-mono">{deal.min_ticket}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Timeline</span><span className="font-mono">{deal.timeline}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Type</span><span className="capitalize">{deal.deal_type}</span></div>
            </div>
            <button onClick={() => setOpen(true)} className="btn-primary w-full mt-6" data-testid="side-request-allocation-button">
              Request Allocation
            </button>
            <p className="text-xs text-muted-foreground text-center mt-3">
              Our team responds within 24–48h.
            </p>
          </div>
        </aside>
      </div>

      {/* Allocation dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="allocation-dialog">
          <DialogHeader>
            <DialogTitle>Request Allocation</DialogTitle>
            <DialogDescription>
              Submit your interest for <span className="text-foreground">{deal.title}</span>. Minimum ticket: <span className="font-mono gold-text">{deal.min_ticket}</span>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <label className="overline block mb-2">Your ticket size</label>
              <input
                value={ticket}
                onChange={(e) => setTicket(e.target.value)}
                placeholder="e.g. $5M"
                className="w-full h-11 bg-transparent border border-border rounded-md px-3 text-sm outline-none focus:border-[hsl(var(--gold))]"
                data-testid="allocation-ticket-input"
              />
            </div>
            <div>
              <label className="overline block mb-2">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-[hsl(var(--gold))]"
                data-testid="allocation-notes-input"
              />
            </div>
          </div>

          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-ghost" data-testid="allocation-cancel">Cancel</button>
            <button onClick={submitAllocation} disabled={!ticket} className="btn-primary" data-testid="allocation-submit">
              Submit Request <ArrowRight className="h-4 w-4" />
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Share dialog */}
      <ShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        type="deal"
        entityId={deal.id}
        entityTitle={deal.title}
        shareText={shareText}
      />

      {/* Term sheet dialog */}
      <Dialog open={tsOpen} onOpenChange={setTsOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="termsheet-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 gold-text" /> Indicative Term Sheet Draft
              {tsScope === "shared" && (
                <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full border border-[hsl(var(--gold))]/40 gold-text font-mono tracking-wider">
                  SHARED DESK DRAFT
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              AI-generated from deal metadata using Claude Sonnet 4.5. Review &amp; edit before sending to counterparties.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 overflow-hidden">
            {/* Versions sidebar */}
            <aside className="hidden md:flex flex-col gap-2 overflow-y-auto" data-testid="termsheet-versions-sidebar">
              <div className="overline text-[9px]">Versions</div>
              {tsVersions.length === 0 ? (
                <div className="text-[11px] text-muted-foreground italic">None yet</div>
              ) : (
                tsVersions.map((v) => {
                  const active = tsScope === "personal" && v.id === tsActiveId;
                  return (
                    <div
                      key={v.id}
                      className={`group rounded-md border px-2.5 py-2 text-xs cursor-pointer transition-colors ${
                        active
                          ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/10"
                          : "border-border hover:border-[hsl(var(--gold))]/40"
                      }`}
                      onClick={() => !active && activateVersion(v.id)}
                      data-testid={`termsheet-version-${v.number}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono tracking-wider">
                          {active && <Check className="inline h-3 w-3 mr-1 gold-text" />}
                          v{v.number}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteVersion(v.id); }}
                          disabled={tsVersions.length <= 1}
                          className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-[hsl(var(--status-closed))] disabled:opacity-0"
                          title="Delete version"
                          data-testid={`termsheet-version-delete-${v.number}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                      <div className="font-mono text-[9px] text-muted-foreground mt-1">
                        {formatRelative(v.generated_at)}
                      </div>
                    </div>
                  );
                })
              )}

              {tsShared && (
                <div className="mt-3 pt-3 border-t border-border">
                  <div className="overline text-[9px] gold-text mb-1 flex items-center gap-1">
                    <Users className="h-3 w-3" /> Shared Desk
                  </div>
                  <div
                    className={`rounded-md border px-2.5 py-2 text-xs cursor-pointer ${
                      tsScope === "shared"
                        ? "border-[hsl(var(--gold))] bg-[hsl(var(--gold))]/10"
                        : "border-border hover:border-[hsl(var(--gold))]/40"
                    }`}
                    onClick={loadSharedDraft}
                    data-testid="termsheet-shared-version"
                  >
                    <div className="font-mono tracking-wider">
                      {tsScope === "shared" && <Check className="inline h-3 w-3 mr-1 gold-text" />}
                      v{tsShared.version_number} · shared
                    </div>
                    <div className="font-mono text-[9px] text-muted-foreground mt-0.5 truncate">
                      by {tsShared.generated_by}
                    </div>
                  </div>
                </div>
              )}
            </aside>

            {/* Draft body */}
            <div className="flex-1 overflow-y-auto border border-border rounded-md p-5 bg-[hsl(var(--surface-elevated))] font-mono text-[13px] leading-relaxed whitespace-pre-wrap" data-testid="termsheet-body">
              {tsLoading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="text-center">
                    <div className="inline-block h-8 w-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" />
                    <div className="mt-4 text-sm text-muted-foreground">Drafting term sheet…</div>
                    <div className="mt-1 text-xs text-muted-foreground">~8–15 seconds</div>
                  </div>
                </div>
              ) : (
                tsDraft
              )}
            </div>
          </div>

          <DialogFooter className="flex-wrap gap-2">
            <div className="text-[10px] text-muted-foreground mr-auto flex items-center gap-2">
              <span>Indicative only. Not a binding offer.</span>
              {tsDraft && tsGeneratedAt && (
                <span className="text-[10px] gold-text font-mono" data-testid="termsheet-meta">
                  · {tsScope === "shared" ? "Shared" : (tsCached ? "Cached" : "Generated")} {formatRelative(tsGeneratedAt)}
                </span>
              )}
            </div>

            {user?.tier === "institutional" && tsDraft && tsScope === "personal" && (
              <button
                onClick={publishShared}
                className="btn-ghost"
                data-testid="termsheet-publish-shared"
                title="Publish this version as the deal's shared draft"
              >
                <Users className="h-4 w-4" /> Publish shared
              </button>
            )}

            {user?.tier === "institutional" && tsDraft && (
              <button
                onClick={sendToCounterparty}
                disabled={tsSending}
                className="btn-ghost"
                data-testid="termsheet-send-counterparty"
                title="Render PDF and attach to Deal Room Documents"
              >
                <Send className={`h-4 w-4 ${tsSending ? "animate-pulse" : ""}`} />
                {tsSending ? "Sending…" : "Send to counterparty"}
              </button>
            )}

            {tsDraft && (
              <button
                onClick={regenerateTermsheet}
                disabled={tsLoading}
                className="btn-ghost"
                data-testid="termsheet-regenerate"
                title="Regenerate draft (saves as a new version)"
              >
                <RotateCw className={`h-4 w-4 ${tsLoading ? "animate-spin" : ""}`} /> Regenerate
              </button>
            )}
            <button onClick={copyTermsheet} disabled={!tsDraft} className="btn-ghost" data-testid="termsheet-copy">
              <CopyIcon className="h-4 w-4" /> Copy
            </button>
            <button onClick={downloadTermsheet} disabled={!tsDraft} className="btn-primary" data-testid="termsheet-download">
              <Download className="h-4 w-4" /> Download .md
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
